// src/screens/AssetsDebts.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Sidebar Data Entry Tool — Pensions & ISAs
// Captures pension and ISA inputs for single clients and couples.
//
// When isCouple === true, renders two columns side by side:
//   Left column  = client pensions + ISAs
//   Right column = partner pensions + ISAs
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import CurrencyInput from '../components/CurrencyInput';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

function TextInput({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div>
      <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-ma-navy/20 focus:border-ma-navy/40"
      />
    </div>
  );
}

function NumberInput({ label, value, onChange, min = 0, max }: {
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number;
}) {
  return (
    <div>
      <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        value={value || ''}
        onChange={e => onChange(parseInt(e.target.value) || 0)}
        className="mt-1 w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm font-bold text-ma-navy outline-none focus:ring-2 focus:ring-ma-navy/20 focus:border-ma-navy/40"
      />
    </div>
  );
}

function DBToggle({ label, isSelected, onToggle }: { label: string; isSelected: boolean; onToggle: () => void }) {
  return (
    <div className="flex items-center justify-between bg-white/60 rounded-lg p-3">
      <span className="text-xs text-gray-700 font-medium">{label}</span>
      <div className="flex gap-2">
        {['DC only', 'DC + DB'].map((option, idx) => (
          <button
            key={idx}
            onClick={onToggle}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition ${
              (idx === 1) === isSelected
                ? 'bg-ma-navy text-white'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AssetsDebts({ data, onUpdate }: Props) {
  const isCouple = data.isCouple === true;

  // ── SINGLE CLIENT ──────────────────────────────────────────────────────────
  if (!isCouple) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-ma-navy">Pensions & ISAs</h2>
          <p className="text-gray-500 mt-1 text-sm">
            Enter your pension and ISA information.
          </p>
        </div>

        <div className="space-y-5">
          {/* PENSIONS */}
          <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-6">
            <h3 className="text-sm font-semibold text-ma-navy mb-4">Pensions</h3>
            <div className="space-y-4">
              <DBToggle
                label="Pension structure"
                isSelected={data.hasDBPension || false}
                onToggle={() => onUpdate({ hasDBPension: !data.hasDBPension })}
              />
              <CurrencyInput
                label="DC pension total (all pots pooled)"
                value={data.pensionBalanceDC || 0}
                onChange={v => onUpdate({ pensionBalanceDC: v, pensionBalance: v })}
              />
              {data.hasDBPension && (
                <>
                  <TextInput label="DB scheme name" value={data.dbPensionScheme || ''} onChange={v => onUpdate({ dbPensionScheme: v })} placeholder="e.g. Teachers' Pension" />
                  <CurrencyInput label="Accrued annual pension at NRA" value={data.dbPensionAccruedAnnual || 0} onChange={v => onUpdate({ dbPensionAccruedAnnual: v })} />
                  <NumberInput label="Normal retirement age" value={data.dbPensionNRA || 67} onChange={v => onUpdate({ dbPensionNRA: v })} min={50} max={75} />
                </>
              )}
              <CurrencyInput label="Monthly employer contribution" value={data.pensionCompanyFunded || 0} onChange={v => onUpdate({ pensionCompanyFunded: v })} />
              <CurrencyInput label="Monthly personal contribution" value={data.pensionMonthly || 0} onChange={v => onUpdate({ pensionMonthly: v })} />
            </div>
          </div>

          {/* ISAs */}
          <div className="rounded-2xl border border-purple-200 bg-purple-50/50 p-6">
            <h3 className="text-sm font-semibold text-ma-navy mb-4">ISAs</h3>
            <div className="space-y-4">
              <CurrencyInput label="Cash ISA balance" value={data.isaCash || 0} onChange={v => onUpdate({ isaCash: v, isaBalance: v + (data.isaStocksShares || 0) })} />
              <CurrencyInput label="Stocks & Shares ISA balance" value={data.isaStocksShares || 0} onChange={v => onUpdate({ isaStocksShares: v, isaBalance: (data.isaCash || 0) + v, investmentsBalance: v })} />
              <CurrencyInput label="Monthly ISA contribution" value={data.isaContribution || 0} onChange={v => onUpdate({ isaContribution: v })} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── COUPLE LAYOUT ──────────────────────────────────────────────────────────
  const clientName = data.clientName || 'Client';
  const partnerName = data.partnerName || 'Partner';

  const combinedDC = (data.pensionBalanceDC || 0) + (data.partnerPensionBalanceDC || 0);
  const combinedISA =
    (data.isaCash || 0) + (data.isaStocksShares || 0) +
    (data.partnerISACash || 0) + (data.partnerISAStocksShares || 0);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">Pensions & ISAs</h2>
        <p className="text-gray-500 mt-1 text-sm">
          Enter pension and ISA information for both {clientName} and {partnerName}.
        </p>
      </div>

      <div className="space-y-5">
        {/* ── PENSIONS — two columns ─────────────────────────────────── */}
        <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-6">
          <h3 className="text-sm font-semibold text-ma-navy mb-5">Pensions</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* CLIENT COLUMN */}
            <div className="space-y-4">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-600 pb-1 border-b border-blue-200 mb-2">
                {clientName}
              </div>

              <DBToggle
                label="Pension structure"
                isSelected={data.hasDBPension || false}
                onToggle={() => onUpdate({ hasDBPension: !data.hasDBPension })}
              />

              <CurrencyInput
                label="DC pension total"
                value={data.pensionBalanceDC || 0}
                onChange={v => onUpdate({ pensionBalanceDC: v, pensionBalance: v })}
              />

              {data.hasDBPension && (
                <>
                  <TextInput label="DB scheme name" value={data.dbPensionScheme || ''} onChange={v => onUpdate({ dbPensionScheme: v })} placeholder="e.g. Teachers' Pension" />
                  <CurrencyInput label="Accrued annual pension" value={data.dbPensionAccruedAnnual || 0} onChange={v => onUpdate({ dbPensionAccruedAnnual: v })} />
                  <NumberInput label="Normal retirement age" value={data.dbPensionNRA || 67} onChange={v => onUpdate({ dbPensionNRA: v })} min={50} max={75} />
                </>
              )}

              <CurrencyInput label="Monthly employer contribution" value={data.pensionCompanyFunded || 0} onChange={v => onUpdate({ pensionCompanyFunded: v })} />
              <CurrencyInput label="Monthly personal contribution" value={data.pensionMonthly || 0} onChange={v => onUpdate({ pensionMonthly: v })} />
            </div>

            {/* PARTNER COLUMN */}
            <div className="space-y-4">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-600 pb-1 border-b border-blue-200 mb-2">
                {partnerName}
              </div>

              <DBToggle
                label="Pension structure"
                isSelected={data.partnerHasDBPension || false}
                onToggle={() => onUpdate({ partnerHasDBPension: !data.partnerHasDBPension })}
              />

              <CurrencyInput
                label="DC pension total"
                value={data.partnerPensionBalanceDC || 0}
                onChange={v => onUpdate({ partnerPensionBalanceDC: v })}
              />

              {data.partnerHasDBPension && (
                <>
                  <TextInput label="DB scheme name" value={data.partnerDBPensionScheme || ''} onChange={v => onUpdate({ partnerDBPensionScheme: v })} placeholder="e.g. Teachers' Pension" />
                  <CurrencyInput label="Accrued annual pension" value={data.partnerDBPensionAccruedAnnual || 0} onChange={v => onUpdate({ partnerDBPensionAccruedAnnual: v })} />
                  <NumberInput label="Normal retirement age" value={data.partnerDBPensionNRA || 67} onChange={v => onUpdate({ partnerDBPensionNRA: v })} min={50} max={75} />
                </>
              )}

              <CurrencyInput label="Monthly employer contribution" value={data.partnerEmployerPensionMonthly || 0} onChange={v => onUpdate({ partnerEmployerPensionMonthly: v })} />
            </div>
          </div>
        </div>

        {/* ── ISAs — two columns ──────────────────────────────────────── */}
        <div className="rounded-2xl border border-purple-200 bg-purple-50/50 p-6">
          <h3 className="text-sm font-semibold text-ma-navy mb-5">ISAs</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* CLIENT COLUMN */}
            <div className="space-y-4">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-600 pb-1 border-b border-purple-200 mb-2">
                {clientName}
              </div>

              <CurrencyInput label="Cash ISA balance" value={data.isaCash || 0} onChange={v => onUpdate({ isaCash: v, isaBalance: v + (data.isaStocksShares || 0) })} />
              <CurrencyInput label="Stocks & Shares ISA balance" value={data.isaStocksShares || 0} onChange={v => onUpdate({ isaStocksShares: v, isaBalance: (data.isaCash || 0) + v, investmentsBalance: v })} />
              <CurrencyInput label="Monthly ISA contribution" value={data.isaContribution || 0} onChange={v => onUpdate({ isaContribution: v })} />
            </div>

            {/* PARTNER COLUMN */}
            <div className="space-y-4">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-600 pb-1 border-b border-purple-200 mb-2">
                {partnerName}
              </div>

              <CurrencyInput label="Cash ISA balance" value={data.partnerISACash || 0} onChange={v => onUpdate({ partnerISACash: v })} />
              <CurrencyInput label="Stocks & Shares ISA balance" value={data.partnerISAStocksShares || 0} onChange={v => onUpdate({ partnerISAStocksShares: v })} />
              <CurrencyInput label="Monthly ISA contribution" value={data.partnerISAMonthly || 0} onChange={v => onUpdate({ partnerISAMonthly: v })} />
            </div>
          </div>
        </div>

        {/* ── Combined summary ────────────────────────────────────────── */}
        <div className="bg-ma-navy rounded-2xl p-5 text-white">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <div className="text-white/60 text-xs uppercase tracking-wide">Combined pension wealth (DC)</div>
              <div className="text-lg font-bold mt-1">{fmt(combinedDC)}</div>
            </div>
            <div>
              <div className="text-white/60 text-xs uppercase tracking-wide">Combined ISA total</div>
              <div className="text-lg font-bold mt-1">{fmt(combinedISA)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
